// src/services/parentService.js
import useAxios from '../utils/useAxios';
import { tenantURL } from '../utils/config';

export const useParentService = () => {
  const axiosInstance = useAxios();

  // ─── ESPACE PARENT ──────────────────────────────────────────────────────
  const getEnfants = async (slug) => {
    try {
      const url = tenantURL(slug, 'mon-espace/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des enfants:', error);
      throw error;
    }
  };

  const getMonCompte = async (slug) => {
    try {
      const url = tenantURL(slug, 'mon-compte/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du compte:', error);
      throw error;
    }
  };

  const getMessagesNonLus = async (slug) => {
    try {
      const url = tenantURL(slug, 'message/messages/non-lus/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des messages non lus:', error);
      throw error;
    }
  };

  const getNotificationsNonLues = async (slug) => {
    try {
      const url = tenantURL(slug, 'notification/notifications/non-lues/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des notifications non lues:', error);
      throw error;
    }
  };

  // ─── BULLETINS ──────────────────────────────────────────────────────────
  const getBulletinsParent = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'gestion/bulletins/mes-bulletins/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des bulletins:', error);
      throw error;
    }
  };

  const getBulletinDetail = async (slug, bulletinId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `gestion/bulletins/${bulletinId}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du bulletin:', error);
      throw error;
    }
  };

  const telechargerBulletinPDF = async (slug, bulletinId) => {
    try {
      const response = await axiosInstance.get(
        tenantURL(slug, `gestion/bulletins/${bulletinId}/pdf/`),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors du téléchargement du bulletin:', error);
      throw error;
    }
  };

  // ─── JUSTIFICATIONS ────────────────────────────────────────────────────
  const getJustificationsEnfant = async (slug, enfantId, params = {}) => {
    try {
      const queryParams = new URLSearchParams({
        ...params,
        eleve: enfantId
      }).toString();
      
      const url = `${tenantURL(slug, 'pointage/justifications/')}?${queryParams}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des justifications:', error);
      throw error;
    }
  };

  const creerJustification = async (slug, data, pieceJointeFile = null) => {
    try {
      let payload = data;
      if (pieceJointeFile) {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) fd.append(key, value);
        });
        fd.append('piece_jointe', pieceJointeFile);
        payload = fd;
      }
      const response = await axiosInstance.post(tenantURL(slug, 'pointage/justifications/'), payload);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la justification:', error);
      throw error;
    }
  };

  const getJustificationDetail = async (slug, justificationId) => {
    try {
      const url = tenantURL(slug, `pointage/justifications/${justificationId}/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la justification:', error);
      throw error;
    }
  };

  const updateJustification = async (slug, justificationId, data, pieceJointeFile = null) => {
    try {
      let payload = data;
      if (pieceJointeFile) {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) fd.append(key, value);
        });
        fd.append('piece_jointe', pieceJointeFile);
        payload = fd;
      }
      const response = await axiosInstance.patch(tenantURL(slug, `pointage/justifications/${justificationId}/`), payload);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la justification:', error);
      throw error;
    }
  };

  const deleteJustification = async (slug, justificationId) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `pointage/justifications/${justificationId}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la justification:', error);
      throw error;
    }
  };

  // ─── PRÉSENCES ─────────────────────────────────────────────────────────
  const getPresencesEnfant = async (slug, enfantId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `pointage/eleve/${enfantId}/presences/`)}?${queryParams}`
        : tenantURL(slug, `pointage/eleve/${enfantId}/presences/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des présences:', error);
      throw error;
    }
  };

  const getStatsPresencesEnfant = async (slug, enfantId, year, month) => {
    try {
      const url = tenantURL(slug, `pointage/eleve/${enfantId}/stats/${year}/${month}/`);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      throw error;
    }
  };

  // ─── MESSAGERIE ────────────────────────────────────────────────────────
  const getMessagesAdministration = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'message/messagerie/admin/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
      throw error;
    }
  };

  const envoyerMessageAdministration = async (slug, contenu, pieceJointeFile = null) => {
    try {
      let payload = { contenu };
      if (pieceJointeFile) {
        const fd = new FormData();
        fd.append('contenu', contenu);
        fd.append('piece_jointe', pieceJointeFile);
        payload = fd;
      }
      const response = await axiosInstance.post(tenantURL(slug, 'message/messagerie/admin/'), payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      throw error;
    }
  };

  const modifierMessageAdministration = async (slug, messageId, contenu) => {
    try {
      const response = await axiosInstance.put(tenantURL(slug, `message/message/${messageId}/`), { contenu });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la modification du message:', error);
      throw error;
    }
  };

  const supprimerMessageAdministration = async (slug, messageId) => {
    try {
      await axiosInstance.delete(tenantURL(slug, `message/message/${messageId}/`));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du message:', error);
      throw error;
    }
  };

  // ─── NOTIFICATIONS ─────────────────────────────────────────────────────
  const getNotifications = async (slug, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, 'notification/notifications-recues/')}?${queryParams}`
        : tenantURL(slug, 'notification/notifications-recues/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      throw error;
    }
  };

  const getNotificationsRecues = async (slug) => {
    try {
      const url = tenantURL(slug, 'notification/notifications-recues/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des notifications reçues:', error);
      throw error;
    }
  };

  const marquerNotificationLue = async (slug, notificationId) => {
    try {
      // Récupérer les notifications les marque comme lues automatiquement
      const response = await axiosInstance.get(tenantURL(slug, 'notification/notifications-recues/'));
      return { success: true };
    } catch (error) {
      console.error('Erreur lors du marquage de la notification:', error);
      throw error;
    }
  };

  const marquerToutesNotificationsLues = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'notification/notifications-recues/'));
      return { success: true, count: response.data?.length || 0 };
    } catch (error) {
      console.error('Erreur lors du marquage des notifications:', error);
      throw error;
    }
  };




// ─── ABONNEMENTS ───────────────────────────────────────────────────────

const getAbonnementsParent = async (slug) => {
  try {
    // ✅ Nouvelle URL pour les parents
    const response = await axiosInstance.get(tenantURL(slug, 'mes-abonnements/'));
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des abonnements:', error);
    throw error;
  }
};


  const getAbonnementDetail = async (slug, abonnementId) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, `abonnements/${abonnementId}/`));
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement de l'abonnement:", error);
      throw error;
    }
  };


const getOffresDisponibles = async (slug) => {
  try {
    // ✅ Nouvelle URL pour les parents
    const response = await axiosInstance.get(tenantURL(slug, 'offres/'));
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des offres:', error);
    throw error;
  }
};

const souscrireOffre = async (slug, data) => {
  try {
    // ✅ Nouvelle URL pour les parents
    const response = await axiosInstance.post(tenantURL(slug, 'abonnements/souscrire/'), data);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la souscription:', error);
    throw error;
  }
};

const renouvelerAbonnement = async (slug, abonnementId) => {
  try {
    // ✅ Nouvelle URL pour les parents
    const response = await axiosInstance.post(tenantURL(slug, `abonnements/${abonnementId}/renouveler/`));
    return response.data;
  } catch (error) {
    console.error('Erreur lors du renouvellement:', error);
    throw error;
  }
};

const resilierAbonnement = async (slug, abonnementId) => {
  try {
    // ✅ Nouvelle URL pour les parents
    const response = await axiosInstance.post(tenantURL(slug, `abonnements/${abonnementId}/resilier/`));
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la résiliation:', error);
    throw error;
  }
};

  // ─── PROFIL ────────────────────────────────────────────────────────────
// ─── PROFIL ────────────────────────────────────────────────────────────
const getProfil = async (slug) => {
  try {
    // mon-profil/ (MonCompteView) : GET + PATCH, ouvert à tous les rôles,
    // utilise MonCompteSerializer (first_name, last_name, email,
    // telephone1, telephone2 — username/id/role en lecture seule).
    const response = await axiosInstance.get(tenantURL(slug, 'mon-profil/'));
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement du profil:', error);
    throw error;
  }
};

const updateProfil = async (slug, data) => {
  try {
    const response = await axiosInstance.patch(tenantURL(slug, 'mon-profil/'), data);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du profil:', error);
    throw error;
  }
};

const changerMotDePasse = async (slug, data) => {
  try {
    // Route dédiée : mon-profil/mot-de-passe/
    // payload attendu : { ancien_mot_de_passe, nouveau_mot_de_passe }
    const response = await axiosInstance.post(
      tenantURL(slug, 'mon-profil/mot-de-passe/'),
      data
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors du changement de mot de passe:', error);
    throw error;
  }
};

  // ─── AIDE & CONTACT ───────────────────────────────────────────────────
  const getArticlesAide = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'aide/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
      throw error;
    }
  };

  const envoyerContact = async (slug, data) => {
    try {
      const response = await axiosInstance.post(tenantURL(slug, 'contact/'), data);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      throw error;
    }
  };




const getEtablissement = async (slug) => {
  try {
    // ✅ Utiliser le bon endpoint pour les parents
    const url = tenantURL(slug, 'etablissements/etablissement/parent/');
    console.log('🔵 getEtablissement URL:', url);
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du chargement de l\'établissement:', error);
    throw error;
  }
};

  // ─── SITUATION FINANCIÈRE ──────────────────────────────────────────────
  const getSituationTousEnfants = async (slug) => {
    try {
      const url = tenantURL(slug, 'caisse/parent/situation-frais/');
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la situation financière:', error);
      throw error;
    }
  };

  const getSituationEnfant = async (slug, enfantId, anneeScolaireId = null) => {
    try {
      let url = tenantURL(slug, `caisse/parent/enfants/${enfantId}/situation-frais/`);
      if (anneeScolaireId) url += `?annee_scolaire_id=${anneeScolaireId}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la situation de l\'enfant:', error);
      throw error;
    }
  };

  const getVersementsEnfant = async (slug, enfantId, anneeScolaireId = null) => {
    try {
      let url = tenantURL(slug, `caisse/parent/enfants/${enfantId}/versements/`);
      if (anneeScolaireId) url += `?annee_scolaire_id=${anneeScolaireId}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des versements:', error);
      throw error;
    }
  };

  const telechargerRecuPDF = async (slug, enfantId, versementId) => {
    try {
      const response = await axiosInstance.get(
        tenantURL(slug, `caisse/parent/enfants/${enfantId}/versements/${versementId}/recu/`),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors du téléchargement du reçu:', error);
      throw error;
    }
  };



  // ─── RETOUR ────────────────────────────────────────────────────────────
  return {
    // Espace parent
    getEnfants,
    getMonCompte,
    getMessagesNonLus,
    getNotificationsNonLues,

    // Bulletins
    getBulletinsParent,
    getBulletinDetail,
    telechargerBulletinPDF,

    // Justifications
    getJustificationsEnfant,
    creerJustification,
    getJustificationDetail,
    updateJustification,
    deleteJustification,

    // Présences
    getPresencesEnfant,
    getStatsPresencesEnfant,

    // Messagerie
    getMessagesAdministration,
    envoyerMessageAdministration,
    modifierMessageAdministration,
    supprimerMessageAdministration,

    // Notifications
    getNotifications,
    getNotificationsRecues,
    marquerNotificationLue,
    marquerToutesNotificationsLues,

    // Abonnements
    getAbonnementsParent,
    getAbonnementDetail,
    getOffresDisponibles,
    souscrireOffre,
    renouvelerAbonnement,
    resilierAbonnement,

    // Profil
    getProfil,
    updateProfil,
    changerMotDePasse,

    // Aide & Contact
    getArticlesAide,
    envoyerContact,

    getEtablissement,


    // Situation financière
    getSituationTousEnfants,
    getSituationEnfant,
    getVersementsEnfant,
    telechargerRecuPDF,

  };
};